/**
 * Main App component with Refine configuration
 *
 * Routes and resources are generated dynamically from module manifests.
 * To hide a module for a customer, set navigation.hidden = true in its manifest.
 */

import React from "react";
import { Refine } from "@refinedev/core";
import { useNotificationProvider } from "@refinedev/antd";
import { customTheme } from "@/theme";
import routerProvider, {
  NavigateToResource,
  UnsavedChangesNotifier,
  DocumentTitleHandler,
} from "@refinedev/react-router-v6";
import { Authenticated } from "@refinedev/core";
import { BrowserRouter, Routes, Route, Outlet, Navigate } from "react-router-dom";
import { ConfigProvider, App as AntdApp, Spin } from "antd";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { createMCPDataProvider } from "@/providers/mcpDataProvider";
import { authProvider } from "@/providers/authProvider";
import { CRMLayout } from "@/components/layout/CRMLayout";

// Dynamic route generation from module registry
import {
  getRefineResources,
  getModuleRoutes,
} from "@/modules/entities/routeConfig";

// Pages (Dashboard and Login are always available)
import { Dashboard } from "@/pages/dashboard";
import { LoginPage } from "@/pages/login";
import { MessagesPage } from "@/pages/messages";
import { EmailsPage } from "@/pages/emails";

// DigiBox portal pages
import { DigiBoxLayout } from "@/pages/digibox/DigiBoxLayout";
import { DigiBoxHub } from "@/pages/digibox/DigiBoxHub";
import { PolicyList } from "@/pages/digibox-policies/PolicyList";
import { PolicyShow } from "@/pages/digibox-policies/PolicyShow";
import { AcceptanceList } from "@/pages/digibox-acceptance/AcceptanceList";
import { AcceptanceAction } from "@/pages/digibox-acceptance/AcceptanceAction";
import { ContentList } from "@/pages/digibox-content/ContentList";
import { ArchiveList } from "@/pages/digibox-archive/ArchiveList";
import { UploadArea } from "@/pages/digibox-upload/UploadArea";
import { TimelinePage } from "@/pages/digibox/TimelinePage";
import { DigiBoxLogin } from "@/pages/digibox/login";

// Configure React Query with optimizations for speed
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

// API URL - will be proxied to backend
const API_URL = "/api";

// Create data provider
const dataProvider = createMCPDataProvider(API_URL);

// Generate resources dynamically from module registry
const resources = getRefineResources();

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ConfigProvider theme={customTheme}>
          <AntdApp>
            <Refine
              routerProvider={routerProvider}
              dataProvider={dataProvider}
              authProvider={authProvider}
              notificationProvider={useNotificationProvider}
              resources={resources}
              options={{
                syncWithLocation: true,
                warnWhenUnsavedChanges: true,
              }}
            >
              <Routes>
                {/* Login route - not protected */}
                <Route path="/login" element={<LoginPage />} />

                {/* DigiBox portal login — not protected */}
                <Route path="/digibox/login" element={<DigiBoxLogin />} />

                {/* DigiBox portal — lightweight layout, no CRM panels */}
                <Route
                  element={
                    <Authenticated
                      key="digibox-routes"
                      fallback={<Navigate to="/digibox/login" replace />}
                      loading={<div className="vault-loading" style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>Loading...</div>}
                    >
                      <DigiBoxLayout />
                    </Authenticated>
                  }
                >
                  <Route path="/digibox" element={<DigiBoxHub />} />
                  <Route path="/digibox/policies" element={<PolicyList />} />
                  <Route path="/digibox/policies/:id" element={<PolicyShow />} />
                  <Route path="/digibox/acceptance" element={<AcceptanceList />} />
                  <Route path="/digibox/acceptance/:id" element={<AcceptanceAction />} />
                  <Route path="/digibox/content" element={<ContentList />} />
                  <Route path="/digibox/archive" element={<ArchiveList />} />
                  <Route path="/digibox/upload" element={<UploadArea />} />
                  <Route path="/digibox/timeline" element={<TimelinePage />} />
                </Route>

                {/* Protected routes */}
                <Route
                  element={
                    <Authenticated
                      key="authenticated-routes"
                      fallback={<LoginPage />}
                      loading={<div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}><Spin size="large" /></div>}
                    >
                      <CRMLayout>
                        <Outlet />
                      </CRMLayout>
                    </Authenticated>
                  }
                >
                  <Route index element={<Dashboard />} />

                  {/* Messaging & Email */}
                  <Route path="/messages" element={<MessagesPage />} />
                  <Route path="/messages/:entityWsid" element={<MessagesPage />} />
                  <Route path="/emails" element={<EmailsPage />} />

                  {/* Dynamic module routes - generated from registry */}
                  {getModuleRoutes()}

                  {/* Catch all */}
                  <Route
                    path="*"
                    element={<NavigateToResource resource="dashboard" />}
                  />
                </Route>
              </Routes>

              <UnsavedChangesNotifier />
              <DocumentTitleHandler
                handler={({ pathname, resource }) => {
                  const baseTitle = "evo-infinito";
                  if (resource?.name) {
                    const resourceName = resource.meta?.label || resource.name;
                    return `${resourceName} | ${baseTitle}`;
                  }
                  if (pathname === "/") {
                    return `Dashboard | ${baseTitle}`;
                  }
                  return baseTitle;
                }}
              />
            </Refine>
          </AntdApp>
        </ConfigProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </BrowserRouter>
  );
};

export default App;
